import {
  $el,
  $list,
  delay,
  getTargets,
  transitionToPromise,
} from 'src/scripts/utils/utils'
import { BaseComponent } from '../base-component'
import { debounce } from 'src/scripts/utils/debounce'
import { isMobile } from 'src/scripts/utils/check-media'
import { isLeftKey, isRightKey } from 'src/scripts/utils/key'

const CART_DRAWER_SELECTOR = 'cart-drawer'
const TRACK_SELECTOR = '[slider-grid-track]'
const SLIDE_SELECTOR = 'slider-grid-slide'
const AUTOPLAY_ATTRIBUTE = 'autoplay-mode'
const AUTOPLAY_INTERVAL_ATTRIBUTE = 'autoplay-interval'
const ONE_AT_TIME_AUTOPLAY_RUNNING_ATTRIBUTE = 'autoplay-running'
const SEAMLESS_AUTOPLAY_RUNNING_ATTRIBUTE = 'seamless-autoplay-running'
const SEAMLESS_AUTOPLAY_PAUSED_ATTRIBUTE = 'seamless-autoplay-paused'
const BUTTON_SELECTOR = 'button[data-direction]'
const BUTTON_DIRECTION_SELECTOR = 'data-direction'
const PROGRESS_BAR_SELECTOR = '[slider-grid-progress]'
const DOT_SELECTOR = '[slider-grid-dot]'

const DESKTOP_DISABLED_ATTRIBUTE = 'desktop-disabled'
const MOBILE_DISABLED_ATTRIBUTE = 'mobile-disabled'

export class SliderGrid extends BaseComponent {
  activeSlide: number
  lastSlideIndex: number
  track: HTMLElement
  trackWidth: number
  navButtons: HTMLButtonElement[]
  buttonsInited: boolean
  withProgressBar: boolean
  dots: HTMLElement[]
  slides: HTMLElement[]
  withDots: boolean
  snaps: number[]
  x: number
  y: number
  autoplay: string | null
  requestFrameId: number
  seamlessRunning: boolean
  pausedAt: number
  resizeObserver: ResizeObserver
  intersectionObserver: IntersectionObserver
  autoplayIntersectionObserver: IntersectionObserver
  autoplayForcedStopped: boolean

  isMobile: boolean
  desktopDisabled: boolean
  mobileDisabled: boolean
  forceDeactivated: boolean

  sidebar: HTMLElement | undefined
  isFocused: boolean

  constructor() {
    super()

    this.activeSlide = 0
    this.autoplay = this.getAttribute(AUTOPLAY_ATTRIBUTE)

    this.desktopDisabled = this.hasAttribute(DESKTOP_DISABLED_ATTRIBUTE)
    this.mobileDisabled = this.hasAttribute(MOBILE_DISABLED_ATTRIBUTE)

    this.sidebar = this.closest(CART_DRAWER_SELECTOR)
  }

  mountComponent(): void {
    this.track = $el(TRACK_SELECTOR, this)

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)
  }

  unmountComponent(): void {
    this.stopAutoplay()
    this.resizeObserver.disconnect()
    this.intersectionObserver?.disconnect()
    this.autoplayIntersectionObserver?.disconnect()
  }

  // INIT

  init = () => {
    this.isMobile = isMobile()
    this.forceDeactivated = false

    if (
      (this.isMobile && this.mobileDisabled) ||
      (!this.isMobile && this.desktopDisabled)
    ) {
      this.toggleListeners(false)
      this.stopAutoplay()
      return
    }

    this.activeSlide = 0
    this.setSnaps()

    if (this.forceDeactivated) {
      this.toggleListeners(false)
      this.setButtonsInactive()
    } else {
      this.initButtons()
      this.toggleButtonsActive()
      this.initProgressBar()
      this.initDots()
      this.initAutoplay()

      this.toggleListeners(true)
    }
  }

  handleResize = debounce(async () => {
    this.track.removeAttribute('style')
    this.stopAutoplay()

    await transitionToPromise(this.track)
    this.init()
  }, 50)

  setSnaps = () => {
    const slides = $list(`[${SLIDE_SELECTOR}]`, this)

    const viewportWidth = this.track.getBoundingClientRect().width + 1
    const { left: firstSlideLeft, width: slideWidth } =
      slides[0].getBoundingClientRect()
    const lastSlideRight =
      slides[slides.length - 1].getBoundingClientRect().right

    const slidesPerView = Math.floor(viewportWidth / slideWidth)
    const snapsCount = slides.length - slidesPerView

    if (snapsCount < 1) {
      this.forceDeactivated = true
      return
    }

    this.snaps = slides.reduce((acc, slide, index) => {
      if (
        index === snapsCount ||
        (index < snapsCount && index % slidesPerView === 0)
      ) {
        let snapPosition = firstSlideLeft - slide.getBoundingClientRect().left

        if (index === snapsCount && snapPosition < viewportWidth) {
          snapPosition = this.isMobile
            ? -(lastSlideRight - viewportWidth + firstSlideLeft)
            : -(lastSlideRight - viewportWidth - firstSlideLeft)
        }

        return [...acc, snapPosition]
      } else {
        return acc
      }
    }, [])

    this.lastSlideIndex = this.snaps.length - 1
    this.trackWidth = firstSlideLeft + lastSlideRight
  }

  // EVENTS

  toggleListeners = (enable: boolean) => {
    if (enable) {
      this.addListener(this, 'mousedown', this.handleMouseDown)
      this.addListener(this, 'mouseup', this.handleMouseUp)
      this.addListener(this, 'dragstart', this.handleDragStart)
      this.addListener(this, 'keydown', this.handleKeydown)
    } else {
      this.removeListener(this, 'mousedown', this.handleMouseDown)
      this.removeListener(this, 'mouseup', this.handleMouseUp)
      this.removeListener(this, 'dragstart', this.handleDragStart)
      this.removeListener(this, 'keydown', this.handleKeydown)
      this.removeListener(this, 'focusin', this.handleFocus)
    }
  }

  handleKeydown = (event: KeyboardEvent): void => {
    if (!this.isFocused) {
      this.stopAutoplay()

      const { lastTarget } = getTargets(this, false)

      this.addListener(lastTarget, 'focusout', this.handleFocusOut)
      this.addListener(this, 'focusin', this.handleFocus)
    }

    if (isLeftKey(event)) {
      this.scrollPrev()
    }

    if (isRightKey(event)) {
      this.scrollNext()
    }

    this.isFocused = true
  }

  handleFocus = (event: FocusEvent): void => {
    const targetLeft = (event.target as HTMLElement).getBoundingClientRect()
      .left
    const thisRight = this.getBoundingClientRect().right

    if (targetLeft > thisRight) {
      this.scrollNext()
    }

    if (targetLeft < 0) {
      this.scrollTo(0)
      this.activeSlide = 0
    }

    this.toggleButtonsActive()
  }

  handleFocusOut = ({ currentTarget }) => {
    this.removeListener(currentTarget, 'focusout', this.handleFocusOut)
    this.removeListener(this, 'focusin', this.handleFocus)
  }

  handleMouseDown = ({ x }) => {
    this.addListener(this, 'mousemove', this.handleMouseMove)
    this.x = x

    this.stopAutoplay()
  }

  handleMouseUp = ({ x }) => {
    if (this.isSwipeRight(x)) {
      this.scrollNext()
    }

    if (this.isSwipeLeft(x)) {
      this.scrollPrev()
    }

    this.classList.remove('dragging')
    this.removeListener(this, 'mousemove', this.handleMouseMove)
  }

  handleMouseMove = ({ x }) => {
    if (this.isSwipeRight(x) || this.isSwipeLeft(x)) {
      this.classList.add('dragging')
    }
  }

  handleDragStart = (event: DragEvent) => {
    event.preventDefault()
    return false
  }

  handleTouchStart = () => {
    const translated = this.track
      .getAttribute('style')
      ?.replace('transform: translateX(', '')
      .replace('px);', '')

    this.stopAutoplay()

    this.track.removeAttribute('style')
    this.track.scrollTo({
      left: Math.abs(+translated),
    })

    this.autoplayForcedStopped = true
  }

  isSwipeRight = (x: number) => {
    return this.x - x > 0
  }

  isSwipeLeft = (x: number) => {
    return this.x - x < 0
  }

  handleMouseEnterAutoplay = () => {
    this.pauseAutoplay()
  }

  handleMouseLeaveAutoplay = () => {
    this.startAutoplay()
  }

  // SCROLLLING

  scrollNext = () => {
    if (!this.canScrollNext()) {
      return
    }

    const nextIndex = this.activeSlide + 1
    this.activeSlide = nextIndex

    this.scrollTo(this.snaps[nextIndex])
  }

  scrollPrev = () => {
    if (!this.canScrollPrev()) {
      return
    }

    const nextIndex = this.activeSlide < 1 ? 0 : this.activeSlide - 1
    this.activeSlide = nextIndex

    this.scrollTo(this.snaps[nextIndex])

    if (this.seamlessRunning) {
      this.seamlessRunning = false
    }
  }

  scrollTo = value => {
    this.track.setAttribute('style', `transform: translateX(${value}px);`)
    this.toggleNavItems()
  }

  canScrollNext = () => {
    return this.activeSlide !== this.lastSlideIndex
  }

  canScrollPrev = () => {
    return this.seamlessRunning || this.activeSlide !== 0
  }

  scrollSlideIntoView = (slideIndex: number) => {
    if (slideIndex === this.activeSlide) {
      return
    }

    if (this.isMobile) {
      this.track.scrollTo({
        left: Math.abs(this.snaps[slideIndex]),
        behavior: 'smooth',
      })

      this.activeSlide = slideIndex
    } else {
      const slideLeft = $list(`[${SLIDE_SELECTOR}]`, this)[
        slideIndex
      ].getBoundingClientRect().left
      const trackLeft = this.track.getBoundingClientRect().left

      const snapToScroll =
        slideIndex === 0
          ? 0
          : slideIndex > this.lastSlideIndex
          ? this.lastSlideIndex
          : this.snaps.findIndex(snap => slideLeft - trackLeft + snap < 10)

      this.scrollTo(this.snaps[snapToScroll])
      this.activeSlide = snapToScroll
    }

    this.toggleNavItems()
  }

  toggleNavItems = () => {
    this.toggleButtonsActive()

    if (this.withProgressBar) {
      this.setProgress()
    }

    if (this.withDots) {
      this.toggleDotsActive(this.activeSlide)
    }
  }

  // AUTOPLAY

  initAutoplay = () => {
    if (this.autoplay) {
      if (this.isMobile) {
        this.addListener(this, 'touchstart', this.handleTouchStart)
      }

      this.addListener(this, 'mouseenter', this.handleMouseEnterAutoplay)
      this.addListener(this, 'mouseleave', this.handleMouseLeaveAutoplay)

      this.autoplayIntersectionObserver = new IntersectionObserver(
        this.toggleAutoplayOnIntersection,
      )

      this.autoplayIntersectionObserver.observe(this)
    }
  }

  toggleAutoplayOnIntersection = (elements: IntersectionObserverEntry[]) => {
    if (elements[0].isIntersecting) {
      this.startAutoplay()
    } else {
      this.pauseAutoplay()

      if (this.autoplayForcedStopped) {
        this.autoplayIntersectionObserver.disconnect()
      }
    }
  }

  startAutoplay = () => {
    if (this.autoplay === 'one_at_time') {
      this.startOneAtTimeAutoPlay()
    } else if (this.autoplay === 'seamless') {
      this.startSeamlessAutoPlay()
      this.toggleButtonsActive()
    }
  }

  stopAutoplay = () => {
    this.removeAutoplayState()

    this.removeListener(this, 'touchstart', this.handleTouchStart)
    this.removeListener(this, 'mouseenter', this.handleMouseEnterAutoplay)
    this.removeListener(this, 'mouseleave', this.handleMouseLeaveAutoplay)
  }

  pauseAutoplay = () => {
    this.removeAutoplayState()

    if (this.autoplay === 'seamless') {
      this.pausedAt = +this.track.style.transform
        .replace('translateX(', '')
        .replace('px)', '')
    }
  }

  removeAutoplayState = () => {
    cancelAnimationFrame(this.requestFrameId)
    this.removeAttribute(ONE_AT_TIME_AUTOPLAY_RUNNING_ATTRIBUTE)
    this.removeAttribute(SEAMLESS_AUTOPLAY_RUNNING_ATTRIBUTE)
  }

  startOneAtTimeAutoPlay = async () => {
    if (this.sidebar) {
      await transitionToPromise(this.sidebar)
      this.setSnaps()
    }

    this.removeAttribute(ONE_AT_TIME_AUTOPLAY_RUNNING_ATTRIBUTE)
    const interval = +this.getAttribute(AUTOPLAY_INTERVAL_ATTRIBUTE) * 1000

    let last: number

    const tick = (timestamp: number): void => {
      if (!last) {
        last = timestamp
      }

      const delta = timestamp - last

      if (delta > interval) {
        if (this.canScrollNext()) {
          this.scrollNext()
        } else {
          this.activeSlide = -1
          this.scrollNext()
        }

        last = timestamp
      }

      this.requestFrameId = requestAnimationFrame(tick)
    }

    this.requestFrameId = requestAnimationFrame(tick)
    this.setAttribute(ONE_AT_TIME_AUTOPLAY_RUNNING_ATTRIBUTE, '')
  }

  startSeamlessAutoPlay = async () => {
    if (this.sidebar) {
      await transitionToPromise(this.sidebar)
      this.setSnaps()
    }

    this.setAttribute(SEAMLESS_AUTOPLAY_RUNNING_ATTRIBUTE, '')

    const distance = this.snaps[this.snaps.length - 1]
    const interval = 0.2 * +this.getAttribute(AUTOPLAY_INTERVAL_ATTRIBUTE)

    let scrolled = this.pausedAt ?? 0

    const tick = (): void => {
      scrolled = Math.max(scrolled - interval, distance)
      this.scrollTo(scrolled)

      if (scrolled != distance) {
        this.requestFrameId = requestAnimationFrame(tick)

        const currentSnap = this.snaps.findIndex(val => {
          const diff = scrolled - val
          return diff > 0 && diff < 20
        })

        if (currentSnap > 0) {
          this.activeSlide = currentSnap
        }

        if (this.withProgressBar) {
          this.setProgress(Math.abs(scrolled / distance) * 100)
        }
      } else {
        this.restartSeamlessAutoplay()
      }
    }

    this.requestFrameId = requestAnimationFrame(tick)
    this.seamlessRunning = true
    this.pausedAt = 0
  }

  restartSeamlessAutoplay = async () => {
    this.setAttribute(SEAMLESS_AUTOPLAY_PAUSED_ATTRIBUTE, '')

    await transitionToPromise(this.track)
    if (!this.isMobile) {
      this.removeAttribute(SEAMLESS_AUTOPLAY_RUNNING_ATTRIBUTE)
    }
    cancelAnimationFrame(this.requestFrameId)
    this.track.removeAttribute('style')
    if (this.withProgressBar) {
      this.setProgress(0)
    }

    await transitionToPromise(this.track)
    this.removeAttribute(SEAMLESS_AUTOPLAY_PAUSED_ATTRIBUTE)

    await delay(500)
    this.pausedAt = 0
    this.activeSlide = 0
    this.startSeamlessAutoPlay()
  }

  // BUTTONS

  initButtons = () => {
    if (this.buttonsInited) {
      this.navButtons.forEach(btn => {
        this.removeListener(btn, 'click', this.handleButtonClick)
      })
    }

    this.navButtons = $list(BUTTON_SELECTOR, this)

    if (this.navButtons.length) {
      this.navButtons.forEach(btn => {
        this.addListener(btn, 'click', this.handleButtonClick)
      })

      this.buttonsInited = true
    }
  }

  handleButtonClick = ({ currentTarget }) => {
    const direction = currentTarget.getAttribute(BUTTON_DIRECTION_SELECTOR)
    direction === 'next' ? this.scrollNext() : this.scrollPrev()
  }

  toggleButtonsActive = () => {
    if (this.buttonsInited) {
      this.navButtons[0].toggleAttribute('disabled', !this.canScrollPrev())
      this.navButtons[1].toggleAttribute('disabled', !this.canScrollNext())
    }
  }

  setButtonsInactive = () => {
    $list(BUTTON_SELECTOR, this).forEach(btn => {
      btn.setAttribute('disabled', '')
    })
  }

  // PROGRESS

  initProgressBar = () => {
    if (this.withProgressBar) {
      this.setProgress(0)
      return
    }

    const progressBar = $el(PROGRESS_BAR_SELECTOR, this)

    if (progressBar) {
      this.setProgress()
      this.withProgressBar = true

      if (this.isMobile) {
        this.addListener(this.track, 'scroll', this.handleProgressOnMobile)
      }
    }
  }

  setProgress = (value?: number) => {
    const progress = value ?? (this.activeSlide / this.lastSlideIndex) * 100
    this.setAttribute('style', `--gsc-slider-grid-progress: ${progress}%;`)
  }

  handleProgressOnMobile = ({ target }) => {
    this.setProgress((target.scrollLeft / this.trackWidth) * 100)
  }

  // DOTS

  initDots = () => {
    if (this.withDots) {
      this.intersectionObserver?.disconnect()

      this.removeListener(
        this.dots[0].parentElement,
        'click',
        this.handleDotTouch,
      )
    }

    if (this.isMobile) {
      this.initMobileDots()
    } else {
      if (this.withDots) {
        this.track.scrollTo({
          left: 0,
          behavior: 'smooth',
        })
      }

      this.initDesktopDots()
    }

    this.toggleDotsActive(0)
  }

  initMobileDots = () => {
    this.dots = $list(DOT_SELECTOR, this).filter(dot => dot.offsetWidth > 0)

    if (this.dots.length > 0) {
      this.slides = $list(`[${SLIDE_SELECTOR}]`, this)

      this.addListener(this.dots[0].parentElement, 'click', this.handleDotTouch)

      this.intersectionObserver = new IntersectionObserver(this.observeSlides, {
        rootMargin: `0px`,
        root: this,
      })

      this.slides.forEach(slide => {
        this.intersectionObserver.observe(slide)
      })

      this.withDots = true
    }
  }

  initDesktopDots = () => {
    this.dots = $list(DOT_SELECTOR, this).filter(dot => dot.offsetWidth > 0)

    if (this.dots.length > 0) {
      this.addListener(this.dots[0].parentElement, 'click', this.handleDotTouch)
      this.withDots = true
    }
  }

  observeSlides = debounce((entries: IntersectionObserverEntry[]): void => {
    if (!this.isMobile) {
      return
    }

    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.toggleDotsActive(+entry.target.getAttribute('index'))
      }
    })
  }, 50)

  handleDotTouch = ({ target }) => {
    const dotClicked = target.closest(DOT_SELECTOR)

    if (this.isMobile) {
      this.track.scrollTo({
        left: Math.abs(this.snaps[+dotClicked.getAttribute('index')]),
        behavior: 'smooth',
      })
    } else {
      this.activeSlide = +dotClicked.getAttribute('index')

      this.scrollTo(this.snaps[this.activeSlide])
      this.toggleDotsActive(this.activeSlide)
      this.toggleButtonsActive()
    }
  }

  toggleDotsActive = (index: number): void => {
    this.dots?.forEach(dot =>
      dot.classList.toggle('is-primary', +dot.getAttribute('index') === index),
    )
  }
}
